import { UploadFile } from 'antd/lib/upload/interface';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { ProjectId } from 'api/models';
import {
  ProjectFileGroup,
  UploadFileWithProgress,
} from 'api/project-files/types';
import { PageLoading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  getProjectFileGroupsQueryKey,
  useCreateProjectFileGroupMutation,
  useLinkFilesToProjectsMutation,
  useProjectFileGroupsQuery,
  useProjectTaskAttachmentsQuery,
  useUpdateProjectFolderMutation,
  useUploadProjectFileMutation,
} from 'queries/project-files';
import { queueMacrotask } from 'utils/macrotask';
import { FileActionHeader } from './action-header';
import {
  CreateGroupFormProps,
  FileCreateGroupModal,
} from './create-group-modal';
import { FileUploadModal, UploadFileFormProps } from './file-upload-modal';
import {
  ProjectFileGroups,
  ProjectFilesUploading,
  ProjectTaskAttachmentsGrid,
} from './grids';
import {
  UpdateFolderFormProps,
  UpdateFolderModal,
} from './update-folder-modal';

const ContentWrapper = styled.div`
  padding-bottom: var(--layout-03);
  > * {
    padding-left: var(--left-spacing) !important;
    padding-right: var(--layout-01) !important;
  }
  height: 100%;
  overflow-y: auto;
`;

export const ProjectFilesPageContent = ({
  projectId,
}: {
  projectId: ProjectId;
}): JSX.Element => {
  const translate = useTranslate();
  const [searchInput, setSearchInput] = useState<string>('');
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState(undefined);
  const [targetFolderForUpdate, setTargetFolderForUpdate] =
    useState<ProjectFileGroup | undefined>(undefined);
  const [filesInProgress, setFilesInProgress] = useState<{
    [id: string]: UploadFileWithProgress;
  }>({});

  const { mutateAsync: createFileGroup, isLoading: isCreatingGroup } =
    useCreateProjectFileGroupMutation(projectId);

  const { mutateAsync: updateFolder, isLoading: isUpdatingFolder } =
    useUpdateProjectFolderMutation(projectId);

  const { mutateAsync: uploadFile, isLoading: isUploadingFile } =
    useUploadProjectFileMutation();

  const { mutateAsync: linkFilesToProjects } =
    useLinkFilesToProjectsMutation(projectId);

  const onFileUploadSuccess = useCallback((fileUid) => {
    queueMacrotask(() => {
      setFilesInProgress(({ [fileUid]: _, ...rest }) => ({
        ...rest,
      }));
    });
  }, []);

  const postUploadFiles = useCallback(
    async (groupId: number, fileIds: number[], fileUids: string[]) => {
      await linkFilesToProjects({
        groupId,
        fileIds,
      });

      fileUids.forEach(onFileUploadSuccess);

      queryClient.invalidateQueries(getProjectFileGroupsQueryKey(projectId));
    },
    [linkFilesToProjects, onFileUploadSuccess, projectId],
  );

  const uploadFiles = useCallback(
    (files: UploadFile[], folderId: number) => {
      return Promise.allSettled(
        files.map(async (file): Promise<{
          file: FileAttachment;
          uid: string;
        }> => {
          setFilesInProgress((prev) => ({
            ...prev,
            [file.uid]: { ...file, progress: 1 },
          }));
          const newFile = await uploadFile({
            file,
            data: {
              projectId: projectId[1],
              folderId,
            },
            reqConfig: {
              // onUploadProgress: (event) => onUploadProgress(event, file.uid),
            },
          });

          return { file: newFile, uid: file.uid };
        }),
      );
    },
    [uploadFile, projectId],
  );

  const onCreateGroupSubmit = useCallback(
    async (formValues: CreateGroupFormProps) => {
      try {
        const newGroup = await createFileGroup(formValues);

        toastService({
          title: translate('project.folder.create.success.title'),
          message: translate('project.folder.create.success.message', {
            folderName: newGroup.folderName,
          }),
        }).success();

        try {
          const uploadResult = await uploadFiles(
            formValues.files,
            newGroup.folderId,
          );

          toastService({
            title: translate('project.files.upload.success.title'),
            message: translate('project.files.upload.success.message', {
              filesCount: uploadResult.length,
              folderName: newGroup.folderName,
            }),
          }).success();

          const fileIds: number[] = [];
          const fileUids: string[] = [];

          uploadResult.forEach((res) => {
            if (res.status === 'fulfilled') {
              fileIds.push(res.value.file.attachmentId);
              fileUids.push(res.value.uid);
            }
          });

          postUploadFiles(newGroup.folderId, fileIds, fileUids);
        } catch (e) {
          toastService({
            title: translate('project.files.upload.error.title'),
            message: translate('project.files.upload.error.message'),
          }).error();
        }
      } catch (e) {
        toastService({
          title: translate('project.folder.create.error.title'),
          message: translate('project.folder.create.error.message'),
        }).error();
      } finally {
        setShowCreateGroupModal(false);
      }
    },
    [createFileGroup, translate, uploadFiles, postUploadFiles],
  );

  const closeEditModal = useCallback(() => {
    setTargetFolderForUpdate(undefined);
  }, []);

  const onUpdateFolderSubmit = useCallback(
    async (formValues: UpdateFolderFormProps, folderId: number) => {
      try {
        await updateFolder({ formValues, folderId });
        toastService({
          title: translate('project.folder.update.success.title'),
          message: translate('project.folder.update.success.message', {
            folderName: formValues.folderName,
          }),
        }).success();

        queryClient.invalidateQueries(getProjectFileGroupsQueryKey(projectId));
      } catch (e) {
        toastService({
          title: translate('project.folder.update.error.title'),
          message: translate('project.folder.update.error.message'),
        }).error();
      } finally {
        closeEditModal();
      }
    },
    [closeEditModal, projectId, translate, updateFolder],
  );

  const onUploadFileSubmit = useCallback(
    async (formValues: UploadFileFormProps) => {
      setShowUploadModal(false);

      const { group, files } = formValues;

      try {
        const uploadResult = await uploadFiles(files, Number(group.value));

        toastService({
          title: translate('project.files.upload.success.title'),
          message: translate('project.files.upload.success.message', {
            filesCount: uploadResult.length,
            folderName: group.folderName,
          }),
        }).success();

        const fileIds: number[] = [];
        const fileUids: string[] = [];

        uploadResult.forEach((res) => {
          if (res.status === 'fulfilled') {
            fileIds.push(res.value.file.attachmentId);
            fileUids.push(res.value.uid);
          }
        });

        postUploadFiles(Number(group.value), fileIds, fileUids);
      } catch (e) {
        toastService({
          title: translate('project.files.upload.error.title'),
          message: translate('project.files.upload.error.message'),
        }).error();
      }
    },
    [postUploadFiles, translate, uploadFiles],
  );

  const onAddFileToGroup = useCallback((groupId) => {
    setTargetGroupId(groupId);
    setShowUploadModal(true);
  }, []);

  const onEditFileGroup = useCallback((folder) => {
    setTargetFolderForUpdate(folder);
  }, []);

  const onUploadModalClose = useCallback(() => {
    setShowUploadModal(false);
    setTargetGroupId(undefined);
  }, []);

  const onCreateFolderClose = useCallback(() => {
    setShowCreateGroupModal(false);
  }, []);

  const onCreateFolderClick = useCallback(() => {
    setShowCreateGroupModal(true);
  }, []);

  const onUploadClick = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  const { data: taskAttachments, isLoading: isLoadingTaskAttachments } =
    useProjectTaskAttachmentsQuery(projectId);
  const { data: folders, isLoading: isLoadingFolders } =
    useProjectFileGroupsQuery(projectId);

  if (isLoadingTaskAttachments || isLoadingFolders) {
    return <PageLoading css />;
  }

  return (
    <ContentWrapper>
      <FileActionHeader
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onCreateGroupClick={onCreateFolderClick}
        onUploadClick={onUploadClick}
      />
      <div>
        <ProjectFilesUploading files={filesInProgress} />
        <ProjectTaskAttachmentsGrid
          projectId={projectId}
          searchInput={searchInput}
          taskAttachments={taskAttachments}
        />
        <ProjectFileGroups
          searchInput={searchInput}
          groups={folders}
          addFileToGroup={onAddFileToGroup}
          editFileGroup={onEditFileGroup}
        />
      </div>

      {showCreateGroupModal && (
        <FileCreateGroupModal
          folders={folders}
          onSubmit={onCreateGroupSubmit}
          onClose={onCreateFolderClose}
          isCreatingGroup={isCreatingGroup}
        />
      )}
      {targetFolderForUpdate && (
        <UpdateFolderModal
          folder={targetFolderForUpdate}
          folders={folders}
          onSubmit={onUpdateFolderSubmit}
          onClose={closeEditModal}
          isUpdating={isUpdatingFolder}
        />
      )}
      {showUploadModal && (
        <FileUploadModal
          onUpload={onUploadFileSubmit}
          onClose={onUploadModalClose}
          groups={folders}
          isUploadingFile={isUploadingFile}
          targetGroupId={targetGroupId}
        />
      )}
    </ContentWrapper>
  );
};
